import React, { useMemo, useState } from 'react';
import { Box } from '@mui/material';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as BarTooltip,
  Label,
} from 'recharts';

import styles, { rechartsStyles, PURPLE, PAIR_COLOR } from './UnitDetailsConsumptionChart.styles';
import { useUnitEnergySummary } from '../../hooks/useUnitEnergySummary';
import { makeDefaultDateRange21 } from '../../utils/dates';

import { dayFmt, monthYearFromDates, fallbackRangeLabel, buildYAxis, computeEffectiveDateRange } from '../../utils/unitDetails.helpers';

export function UnitDetailsConsumptionLegend() {
  const entries = [
    { label: 'AC Energy', color: PURPLE },
    { label: 'DC Energy', color: PAIR_COLOR },
  ];
  return (
    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
      {entries.map((e) => (
        <Box key={e.label} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 10, height: 10, borderRadius: 4, backgroundColor: e.color }} />
          <span style={{ fontSize: 11, opacity: 0.85 }}>{e.label}</span>
        </Box>
      ))}
    </Box>
  );
}

export default function UnitEnergyConsumptionChart({ unitName, dateRange }) {
  const [localRange] = useState(() => makeDefaultDateRange21());

  const effectiveRange = useMemo(
    () => computeEffectiveDateRange(dateRange, undefined, undefined, localRange),
    [dateRange, localRange]
  );

  const { data: fetchedData } = useUnitEnergySummary({
    unitName,
    range: effectiveRange, 
  });

  const parsedDates = useMemo(() => {
    if (!Array.isArray(fetchedData)) return [];
    return fetchedData
      .map((r) => (r?.date instanceof Date ? r.date : null))
      .filter((d) => d && !isNaN(d));
  }, [fetchedData]);

  const monthYearLabel = useMemo(() => {
    const lbl = monthYearFromDates(parsedDates);
    return lbl || fallbackRangeLabel(effectiveRange); 
  }, [parsedDates, effectiveRange]);

  const chartData = useMemo(() => {
    if (!Array.isArray(fetchedData) || fetchedData.length === 0) {
      return [{ day: '', fullDate: '', ac: null, dc: null }];
    }
    return fetchedData.map((r) => {
      const d = r?.date instanceof Date ? r.date : null;
      const day = d && !isNaN(d) ? dayFmt.format(d) : '';
      const fullDate = d && !isNaN(d) ? d.toLocaleDateString('en-GB') : '';
      return { day, fullDate, ac: r.ac, dc: r.dc };
    });
  }, [fetchedData]);

  const manyPoints = chartData.length > 16;

  const labelFormatter = (_, payload) => {
    const p = payload && payload[0] && payload[0].payload;
    return p?.fullDate || '';
  };

  const formatTooltip = (value, _name, entry) => {
    const label = entry?.dataKey === 'ac' ? 'AC Energy' : 'DC Energy';
    return [value == null ? '—' : `${value} kWh`, label];
  };

  const { yMax, ticks } = useMemo(() => {
    const vals = chartData.flatMap((d) => [d.ac ?? 0, d.dc ?? 0]);
    return buildYAxis(vals, 500, 2000);
  }, [chartData]);

  const yAxisWidth = useMemo(() => {
    const labels = (ticks && ticks.length ? ticks : [0]).map((t) =>
      Number.isFinite(t) ? t.toLocaleString('en-GB') : String(t)
    );
    const maxChars = Math.max(...labels.map((s) => s.length), 3);
    const charPx = 7.2;
    const pad = 18;
    const base = 44;
    return Math.max(base, Math.ceil(maxChars * charPx) + pad);
  }, [ticks]);

  const xAxisHeight = useMemo(() => {
    const longestTick = chartData.reduce((acc, d) => {
      const label = d.day || '';
      return label.length > acc ? label.length : acc;
    }, 0);
    const base = manyPoints ? 28 : 22;
    const extra = Math.min(longestTick * 1.2, 10);
    return base + extra;
  }, [chartData, manyPoints]);

  return (
    <Box sx={{ ...styles.container, ...styles.chartText }}>
      <Box sx={styles.chartWrapper}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={chartData}
            margin={rechartsStyles.margin}
            barCategoryGap="18%"
            maxBarSize={64}
          >
            <XAxis
              dataKey="day"
              interval={0}
              minTickGap={4}
              tickMargin={10}
              height={xAxisHeight}
              tick={{ fontSize: 10, dy: 6 }}
            >
              <Label
                value={monthYearLabel}
                position="insideBottom"
                dy={24}
                style={{ textAnchor: 'middle', fontSize: 11, opacity: 0.75 }}
              />
            </XAxis>

            <YAxis domain={[0, yMax]} ticks={ticks} tick={{ fontSize: 10 }} width={yAxisWidth}>
              <Label
                value="kWh"
                angle={-90}
                position="insideLeft"
                offset={2}
                style={{ textAnchor: 'middle', fontSize: 10, opacity: 0.75 }}
              />
            </YAxis>

            <BarTooltip
              labelFormatter={labelFormatter}
              formatter={formatTooltip}
              wrapperStyle={rechartsStyles.tooltip}
            />
            <Bar dataKey="ac" name="AC Energy" fill={PURPLE} />
            <Bar dataKey="dc" name="DC Energy" fill={PAIR_COLOR} />
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
}
